import { ArrowLeft, ArrowRight, Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { api } from "./api";
import { navigate } from "./App";
import Logo from "./components/Logo";

const REMEMBERED_USERNAME = "tl-remembered-username";
function rememberedUsername() {
  try { return window.localStorage.getItem(REMEMBERED_USERNAME) || ""; }
  catch { return ""; }
}

export default function LoginPage() {
  const [username, setUsername] = useState(rememberedUsername);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => Boolean(rememberedUsername()));
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Đăng nhập | Thầy Linh dạy Toán";
    api.session().then((session) => {
      if (session.authenticated) navigate("/quan-ly");
    }).catch(() => undefined);
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.login(username, password, remember);
      try {
        if (remember) window.localStorage.setItem(REMEMBERED_USERNAME, username.trim());
        else window.localStorage.removeItem(REMEMBERED_USERNAME);
      } catch { /* Đăng nhập vẫn hoạt động khi trình duyệt chặn localStorage. */ }
      navigate("/quan-ly");
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "Không thể đăng nhập.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page">
      <div className="login-page__art" aria-hidden="true">
        <Logo light />
        <div className="login-art-copy">
          <span>CỔNG QUẢN LÝ HỌC TẬP</span>
          <h1>Dữ liệu rõ ràng.<br /><em>Quyết định tốt hơn.</em></h1>
          <p>Theo dõi từng điểm số, nhận ra từng tín hiệu và đồng hành sát hơn với mỗi học sinh.</p>
        </div>
        <div className="login-formula login-formula--one">∫ f(x)dx</div>
        <div className="login-formula login-formula--two">Δ = b² − 4ac</div>
        <svg viewBox="0 0 600 240" className="login-graph">
          <path d="M20 220 C120 210 110 30 225 95 S390 240 575 25" fill="none" stroke="#f0cc7d" strokeWidth="4" />
          <circle cx="225" cy="95" r="7" fill="#ef7455" />
        </svg>
        <div className="login-art-footer"><ShieldCheck size={19} /> Dữ liệu được bảo vệ và lưu trữ an toàn</div>
      </div>
      <div className="login-page__form-wrap">
        <button className="back-button" onClick={() => navigate("/")}><ArrowLeft size={17} /> Về trang chủ</button>
        <form className="login-form" onSubmit={submit}>
          <div className="login-icon"><LockKeyhole /></div>
          <span className="login-kicker">XIN CHÀO THẦY LINH</span>
          <h2>Đăng nhập quản lý</h2>
          <p>Sử dụng tài khoản giáo viên để truy cập bảng điểm.</p>

          <label>
            <span>Tên đăng nhập</span>
            <input name="username" placeholder="Nhập tên đăng nhập" value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" autoCapitalize="none" spellCheck={false} required />
          </label>
          <label>
            <span>Mật khẩu</span>
            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Nhập mật khẩu"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
              <button type="button" aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"} onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>
          <label className="login-remember">
            <input type="checkbox" checked={remember} onChange={(event) => {
              setRemember(event.target.checked);
              if (!event.target.checked) {
                try { window.localStorage.removeItem(REMEMBERED_USERNAME); } catch { /* Bộ nhớ có thể bị chặn. */ }
              }
            }} />
            <span><b>Ghi nhớ đăng nhập</b><small>Lưu tên tài khoản và giữ đăng nhập 30 ngày trên trình duyệt này.</small></span>
          </label>
          {error && <div className="form-error" role="alert">{error}</div>}
          <button className="login-submit" disabled={loading}>
            {loading ? <span className="spinner" /> : <>Vào bảng điều khiển <ArrowRight size={18} /></>}
          </button>
        </form>
        <small className="login-copyright">© {new Date().getFullYear()} Thầy Linh dạy Toán</small>
      </div>
    </main>
  );
}
